import type { CSSProperties } from "react";
import { Link } from "react-router";

export function AboutPage() {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #FAFAFA 0%, #FFFFFF 100%)",
      }}
    >
      <header
        style={{
          textAlign: "center",
          padding: "5rem 1.5rem 3rem",
          maxWidth: 720,
          margin: "0 auto",
        }}
      >
        <p
          style={{
            fontSize: "0.9rem",
            fontWeight: 600,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "#F05260",
            marginBottom: "0.75rem",
          }}
        >
          The Science Behind SARA&apos;s Books
        </p>
        <h1
          style={{
            fontSize: "clamp(2.2rem, 5vw, 3.2rem)",
            fontWeight: 800,
            lineHeight: 1.15,
            color: "#1a1a1a",
            marginBottom: "1rem",
          }}
        >
          Read First. The Rest Follows.
        </h1>
        <p
          style={{
            fontSize: "clamp(1.05rem, 2.5vw, 1.25rem)",
            color: "#555",
            lineHeight: 1.7,
            maxWidth: 600,
            margin: "0 auto",
          }}
        >
          If your child is struggling with reading, it doesn&apos;t mean
          anything is wrong with them. It may simply mean they need a different
          approach. <strong>Engaged Aided Reading (EAR)</strong> works with how
          the brain naturally learns to recognize words — through real stories,
          not drills.
        </p>
      </header>

      <article
        style={{
          maxWidth: 800,
          margin: "0 auto",
          padding: "0 1.5rem 4rem",
          color: "#333",
          lineHeight: 1.75,
        }}
      >
        <blockquote
          style={{
            margin: "0 0 3rem",
            padding: "1.5rem 2rem",
            background: "#FFF8F0",
            borderLeft: "4px solid #F05260",
            borderRadius: "0 8px 8px 0",
            fontStyle: "italic",
            fontSize: "1.15rem",
            color: "#5C4033",
            lineHeight: 1.7,
          }}
        >
          &ldquo;Read to learn, don&apos;t learn to read — stories plant the
          strongest seed.&rdquo;
        </blockquote>

        <section style={{ marginBottom: "3rem" }}>
          <h2 style={sectionHeading}>The Core Idea</h2>
          <p style={{ marginBottom: "1rem" }}>
            Think about how you read right now. You don&apos;t sound out each
            word — you recognize them instantly, like recognizing a familiar
            face. Your child can learn to read the same way.
          </p>
          <p>
            EAR helps children build a library of instantly recognizable words —
            what researchers call{" "}
            <em style={{ color: "#842885" }}>sight vocabulary</em> — through
            simple, supported reading experiences. Instead of asking children to
            decode every unfamiliar word, EAR keeps them immersed in real
            stories while their word recognition grows naturally.
          </p>
        </section>

        <section style={{ marginBottom: "3rem" }}>
          <h2 style={sectionHeading}>How It Works</h2>
          <p style={{ marginBottom: "1.5rem" }}>
            When your child encounters an unfamiliar word while reading with
            SARA:
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "1rem",
              marginBottom: "1.5rem",
            }}
          >
            {[
              {
                num: "1",
                title: "Pause",
                body: "Stop at the word. No guessing — ever.",
                accent: "#F05260",
              },
              {
                num: "2",
                title: "Name the Letters",
                body: 'Say each letter aloud: "b... r... i... g... h... t"',
                accent: "#84D5F7",
              },
              {
                num: "3",
                title: "Hear the Word",
                body: 'SARA says the word: "bright." Your child repeats it.',
                accent: "#4EB969",
              },
              {
                num: "4",
                title: "Keep Reading",
                body: "Back to the story. Flow maintained, confidence intact.",
                accent: "#842885",
              },
            ].map((step) => (
              <div
                key={step.num}
                style={{
                  background: "white",
                  borderRadius: "12px",
                  padding: "1.25rem",
                  border: "1px solid #eee",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    background: step.accent,
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    fontSize: "1rem",
                    marginBottom: "0.75rem",
                  }}
                >
                  {step.num}
                </div>
                <h3
                  style={{
                    fontSize: "1.05rem",
                    fontWeight: 700,
                    marginBottom: "0.25rem",
                    color: "#1a1a1a",
                  }}
                >
                  {step.title}
                </h3>
                <p style={{ fontSize: "0.9rem", color: "#666", margin: 0 }}>
                  {step.body}
                </p>
              </div>
            ))}
          </div>
          <p style={{ color: "#555" }}>
            That&apos;s it. No sounding out. No struggling. No frustration. Just
            attention to letters, then the word — and back to the story.
          </p>
        </section>

        <section style={{ marginBottom: "3rem" }}>
          <h2 style={sectionHeading}>What This Looks Like</h2>
          <div
            style={{
              background: "#F8F9FA",
              borderRadius: "12px",
              padding: "1.5rem 2rem",
              border: "1px solid #eee",
              fontSize: "1.05rem",
            }}
          >
            <p style={{ marginBottom: "0.75rem" }}>
              <strong style={{ color: "#842885" }}>Child:</strong> &ldquo;The
              sky was...&rdquo;{" "}
              <span style={{ color: "#999", fontStyle: "italic" }}>
                (pauses at &ldquo;bright&rdquo;)
              </span>
            </p>
            <p style={{ marginBottom: "0.75rem" }}>
              <strong style={{ color: "#F05260" }}>Parent:</strong> &ldquo;Can
              you name each letter?&rdquo;
            </p>
            <p style={{ marginBottom: "0.75rem" }}>
              <strong style={{ color: "#842885" }}>Child:</strong> &ldquo;b...
              r... i... g... h... t&rdquo;
            </p>
            <p style={{ marginBottom: "0.75rem" }}>
              <strong style={{ color: "#F05260" }}>Parent:</strong>{" "}
              &ldquo;Bright.&rdquo;
            </p>
            <p style={{ margin: 0 }}>
              <strong style={{ color: "#842885" }}>Child:</strong> &ldquo;The
              sky was bright. The sun was...&rdquo;
            </p>
          </div>
        </section>

        <section style={{ marginBottom: "3rem" }}>
          <h2 style={sectionHeading}>Why Letter Naming Matters</h2>
          <p style={{ marginBottom: "1rem" }}>
            The letter-naming step isn&apos;t busywork. When your child names
            each letter, their brain is creating a mental &ldquo;photograph
            &rdquo; of the word. This attention to the word&apos;s exact letter
            sequence — its <em>alphabetic identity</em> — is what allows the
            brain to recognize it instantly next time.
          </p>
          <p style={{ marginBottom: "1rem" }}>
            Neuroscience research has identified the{" "}
            <strong>visual word form area</strong> in the brain — a region that
            processes written words as unified visual objects, not sequences of
            sounds. Word recognition here happens in hundredths of a second,{" "}
            <em>before</em> phonological processing begins. This is why fluent
            reading doesn&apos;t feel like &ldquo;sounding out&rdquo; — it feels
            like recognition.
          </p>
          <p>
            As reading researcher Linnea Ehri observed: &ldquo;Any word that is
            read sufficiently often becomes a sight word that is read from
            memory.&rdquo; EAR accelerates this natural process by ensuring
            every encounter with a new word includes focused attention on its
            letters.
          </p>
        </section>

        <section
          style={{
            marginBottom: "3rem",
            padding: "1.75rem",
            background: "#FFF5F5",
            borderRadius: "12px",
            borderLeft: "4px solid #F05260",
          }}
        >
          <h2
            style={{
              fontSize: "1.4rem",
              fontWeight: 700,
              marginBottom: "0.75rem",
              color: "#2c3e50",
            }}
          >
            The One Critical Rule: No Guessing
          </h2>
          <p style={{ marginBottom: "1rem" }}>
            This is essential:{" "}
            <strong>never encourage guessing at words</strong>. Guessing creates
            inaccurate memories of words, introduces confusion between
            similar-looking words, and undermines comprehension.
          </p>
          <p>
            Instead, teach your child that pausing at unknown words and asking
            for help is <em>always</em> the right thing to do. When they pause
            and ask, <strong>celebrate that moment</strong>. They&apos;re doing
            exactly what strong readers do — they&apos;re being precise, not
            impulsive.
          </p>
        </section>

        <section style={{ marginBottom: "3rem" }}>
          <h2 style={sectionHeading}>Building a Reader&apos;s Identity</h2>
          <p style={{ marginBottom: "1rem" }}>
            One of the most powerful gifts you can give your child is the
            identity of being a reader — not someday, but <em>right now</em>.
            When reading feels fluent, even with support, children see
            themselves as capable. This confidence fuels motivation, which fuels
            more reading, which builds more skill.
          </p>
          <p>
            With EAR, your child experiences success from the very first reading
            session. They&apos;re not failing at sounding out words —
            they&apos;re succeeding at reading stories.
          </p>
        </section>

        <blockquote
          style={{
            margin: "0 0 3rem",
            padding: "1.5rem 2rem",
            background: "#F0F4FF",
            borderLeft: "4px solid #84D5F7",
            borderRadius: "0 8px 8px 0",
            fontStyle: "italic",
            fontSize: "1.15rem",
            color: "#2A3B5C",
            lineHeight: 1.7,
          }}
        >
          &ldquo;You&apos;re a reader from day one — learning happens while you
          have fun.&rdquo;
        </blockquote>

        <section style={{ marginBottom: "3rem" }}>
          <h2 style={sectionHeading}>The Research</h2>
          <p style={{ marginBottom: "1rem" }}>
            In fourth grade, more than one in three students test below basic
            levels of literacy. The dominant approach to reading instruction
            emphasizes phonics, and while it works well for many, a significant
            portion of learners do not respond — what the National Reading Panel
            characterized as showing &ldquo;no significant impact&rdquo; for
            low-achieving readers in grades 2&ndash;6.
          </p>
          <p style={{ marginBottom: "1rem" }}>
            EAR offers an evidence-informed alternative. In a pilot study with
            struggling readers (grades 4&ndash;5, including English language
            learners), students using EAR learned new sight words after an
            average of <strong>1.6 exposures</strong> — compared to the
            4&ndash;14 exposures typically required with traditional methods.
            Over 45 days, students learned between 15 and 49 new sight words
            each while reading fewer than 2,500 words.
          </p>
          <p>
            EAR isn&apos;t positioned against phonics. It offers an{" "}
            <strong>alternative pathway</strong> for students who don&apos;t
            respond to phonics-based intervention, a{" "}
            <strong>complementary approach</strong> alongside systematic
            phonics, and a <strong>foundation</strong> of sight vocabulary that
            may make later phonics instruction more effective.
          </p>
        </section>

        <section style={{ marginBottom: "3rem" }}>
          <h2 style={sectionHeading}>Tips for Success</h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "0.75rem",
            }}
          >
            {[
              {
                text: "Read together daily — even 10-15 minutes makes a difference",
              },
              {
                text: "Choose books your child loves — interest drives engagement",
              },
              {
                text: "Keep it stress-free — reading should feel like a gift, not a chore",
              },
              {
                text: "Praise effort and attention — not just accuracy",
              },
              {
                text: "Let them reread favorites — repetition builds fluency naturally",
              },
              {
                text: "Use SARA's app — your child taps unfamiliar words to hear them spoken",
              },
            ].map((tip) => (
              <div
                key={tip.text}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0.5rem",
                  padding: "0.75rem 1rem",
                  background: "#F8F9FA",
                  borderRadius: "8px",
                  fontSize: "0.95rem",
                }}
              >
                <span
                  style={{
                    color: "#4EB969",
                    fontWeight: 700,
                    flexShrink: 0,
                    marginTop: "1px",
                  }}
                >
                  +
                </span>
                <span>{tip.text}</span>
              </div>
            ))}
          </div>
        </section>

        <section style={{ marginBottom: "3rem" }}>
          <h2 style={sectionHeading}>Designed for Every Learner</h2>
          <p style={{ marginBottom: "1rem" }}>
            SARA&apos;s Books is designed around{" "}
            <strong>Universal Design for Learning</strong> principles. The
            platform tracks each reader&apos;s sight vocabulary in real-time and
            recommends texts at the right challenge level — not too easy, not
            too hard — so every session feels like success.
          </p>
          <p>
            Support is provided at the point of need, not in isolation.
            Just-in-time word identification preserves reading flow and
            comprehension while building the sight vocabulary that underlies
            fluent reading. For educators, rich data about individual and
            class-wide sight vocabularies helps inform instruction and identify
            teachable moments.
          </p>
        </section>

        <section
          style={{
            textAlign: "center",
            padding: "2.5rem 2rem",
            background: "linear-gradient(135deg, #F05260 0%, #F47781 100%)",
            borderRadius: "16px",
            color: "white",
          }}
        >
          <h2
            style={{
              fontSize: "1.6rem",
              fontWeight: 800,
              marginBottom: "0.5rem",
            }}
          >
            Every story starts with a single page
          </h2>
          <p
            style={{
              marginBottom: "1.5rem",
              opacity: 0.95,
              fontSize: "1.05rem",
            }}
          >
            Try SARA&apos;s Books with your child — free, no account required.
          </p>
          <Link
            to="/"
            style={{
              display: "inline-block",
              padding: "0.875rem 2.5rem",
              background: "white",
              color: "#F05260",
              borderRadius: "999px",
              textDecoration: "none",
              fontWeight: 700,
              fontSize: "1.05rem",
              boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
            }}
          >
            Start Reading Free
          </Link>
        </section>

        <p
          style={{
            textAlign: "center",
            marginTop: "3rem",
            fontStyle: "italic",
            color: "#999",
            fontSize: "1rem",
          }}
        >
          Read by ear. Read for life.
        </p>
      </article>
    </div>
  );
}

const sectionHeading: CSSProperties = {
  fontSize: "1.6rem",
  fontWeight: 700,
  marginBottom: "1rem",
  color: "#1a1a1a",
};
